// Budget de l'exercice (M06, lot L4-B) : état et actes (proposer, adopter avec la délibération,
// rendre exécutoire, clore), synthèse par section, lignes avec leurs cinq montants calculés,
// décisions modificatives tracées, journal des états et exports des états budgétaires (CSV, XLSX).
// Montants « (fictif) » pour un budget de présentation.
import { ACTIONS_BUDGET, ETATS_BUDGET_LIGNES_MODIFIABLES, type ActionBudget, type Budget, type SectionBudget } from '@cc/contrats';
import { Bandeau, Bouton, EtatVide, LienBouton, PuceEtat, Selection } from '@cc/ui';
import { Download, FileUp, Landmark, Plus, SlidersHorizontal } from 'lucide-react';
import { useEffect, useState } from 'react';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date, dateHeure } from '../../commun/format';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { telechargerEtat, useBudget, useBudgets, useDecisionsModificatives, useLignes } from './api';
import { FormulaireBudget, FormulaireDecisionModificative, FormulaireLigne, FormulaireTransition } from './FormulairesBudget';
import { francs, libelleEtatBudget, TON_BUDGET } from './logique';
import { NavigationBudget } from './NavigationBudget';
import { SyntheseBudget, TableauLignes } from './TableauLignes';
import styles from './budget.module.css';

function DecisionsModificatives({ budget }: { budget: Budget }) {
  const decisions = useDecisionsModificatives(budget.id);
  const liste = decisions.data?.elements ?? [];
  return (
    <section className={styles.bloc} aria-labelledby="titre-dm">
      <h2 id="titre-dm">Décisions modificatives</h2>
      {liste.length === 0 ? <p className={styles.note}>Aucune décision modificative sur ce budget.</p> : (
        <ul className={styles.decisions}>
          {liste.map((d) => (
            <li key={d.reference}>
              <strong>{d.reference}</strong> du {date(d.date)}{d.deliberation_reference ? ` (${d.deliberation_reference})` : ''} : {d.motif}
              <ul>
                {d.mouvements.map((m) => <li key={m.id}><span className="cc-mono">{m.ligne.code}</span> {m.ligne.libelle} : {m.montant > 0 ? '+' : ''}{francs(m.montant, budget.fictif)}</li>)}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Exports({ budget }: { budget: Budget }) {
  const [section, setSection] = useState<'' | SectionBudget>('');
  const [erreur, setErreur] = useState<string | null>(null);
  const lancer = (etat: 'balance' | 'mandats', format: 'csv' | 'xlsx') => {
    setErreur(null);
    telechargerEtat(budget.id, etat, format, section || undefined).catch(() => setErreur('L\'export n\'a pas pu être produit.'));
  };
  return (
    <section className={styles.bloc} aria-labelledby="titre-exports">
      <h2 id="titre-exports">États budgétaires</h2>
      <div className={styles.exports}>
        <Selection libelle="Section exportée" options={[{ valeur: '', libelle: 'Toutes les sections' }, { valeur: 'fonctionnement', libelle: 'Fonctionnement' }, { valeur: 'investissement', libelle: 'Investissement' }]} value={section} onChange={(e) => setSection(e.target.value as '' | SectionBudget)} />
        <Bouton variante="secondaire" icone={Download} onClick={() => lancer('balance', 'csv')}>Balance CSV</Bouton>
        <Bouton variante="secondaire" icone={Download} onClick={() => lancer('balance', 'xlsx')}>Balance XLSX</Bouton>
        <Bouton variante="secondaire" icone={Download} onClick={() => lancer('mandats', 'csv')}>Journal des mandats CSV</Bouton>
        <Bouton variante="secondaire" icone={Download} onClick={() => lancer('mandats', 'xlsx')}>Journal des mandats XLSX</Bouton>
      </div>
      {erreur ? <Bandeau type="erreur">{erreur}</Bandeau> : null}
    </section>
  );
}

export function PageBudget() {
  const { contexte } = useSession();
  const budgets = useBudgets();
  const [choisi, setChoisi] = useState<string | null>(null);
  const liste = budgets.data?.elements ?? [];
  useEffect(() => {
    if (!choisi && liste.length > 0) setChoisi(liste[0]?.id ?? null);
  }, [choisi, liste]);
  const budget = useBudget(choisi);
  const lignes = useLignes(choisi);
  const [creation, setCreation] = useState(false);
  const [ligne, setLigne] = useState(false);
  const [dm, setDm] = useState(false);
  const [acte, setActe] = useState<ActionBudget | null>(null);
  const b = budget.data;
  const exercice = new Date().getFullYear();
  const peutCreer = peut(contexte, 'budget', 'creer');
  const actions = (
    <>
      {liste.length > 1 ? <Selection libelle="Exercice" options={liste.map((x) => ({ valeur: x.id, libelle: String(x.exercice) }))} value={choisi ?? ''} onChange={(e) => setChoisi(e.target.value)} /> : null}
      {peutCreer && !liste.some((x) => x.exercice === exercice) ? <Bouton variante={liste.length ? 'secondaire' : 'principal'} icone={Plus} onClick={() => setCreation(true)}>Budget {exercice}</Bouton> : null}
    </>
  );
  return (
    <>
      <EnTetePage etiquette="Finances et budget" titre={b ? `${b.libelle}${b.fictif && !b.libelle.includes('(fictif)') ? ' (fictif)' : ''}` : 'Budget'} description={b ? <>Exercice {b.exercice} · <PuceEtat ton={TON_BUDGET[b.etat]} libelle={libelleEtatBudget(b.etat)} />{b.deliberation_reference ? ` · ${b.deliberation_reference} du ${date(b.adopte_le)}` : ''}{b.transmis_le ? ` · transmis le ${date(b.transmis_le)}` : ''}</> : 'Budget par exercice, lignes et cinq montants calculés.'} actions={actions} />
      <NavigationBudget actif="budget" />
      <EtatRequete chargement={budgets.isPending} erreur={budgets.error} onReessayer={() => void budgets.refetch()} lot="L4-B" fonction="Le budget">
        {liste.length === 0 ? (
          <EtatVide titre="Aucun budget" icone={Landmark}><p>Aucun budget n&apos;est encore créé dans cette collectivité.{peutCreer ? ' Créez le budget de l\'exercice, puis saisissez ou importez ses lignes.' : ''}</p></EtatVide>
        ) : !b ? (
          // Fiche du budget choisi : chargement ou erreur affichés (jamais une page muette).
          <EtatRequete chargement={!budget.error} erreur={budget.error} onReessayer={() => void budget.refetch()} lot="L4-B" fonction="Le budget de l'exercice">
            {null}
          </EtatRequete>
        ) : (
          <>
            <div className={styles.actes}>
              {peut(contexte, 'budget', 'valider') ? b.actions.map((a) => <Bouton key={a} variante={a === 'renvoyer' ? 'secondaire' : 'principal'} onClick={() => setActe(a)}>{ACTIONS_BUDGET[a].libelle}</Bouton>) : null}
              {peutCreer && b.etat !== 'clos' ? <Bouton variante="secondaire" icone={Plus} onClick={() => setLigne(true)}>Nouvelle ligne</Bouton> : null}
              {peutCreer && ETATS_BUDGET_LIGNES_MODIFIABLES.includes(b.etat) ? <LienBouton variante="secondaire" icone={FileUp} href={`/budget/import?budget=${b.id}`}>Importer la nomenclature</LienBouton> : null}
              {peut(contexte, 'budget', 'modifier') && (b.etat === 'adopte' || b.etat === 'executoire') ? <Bouton variante="secondaire" icone={SlidersHorizontal} onClick={() => setDm(true)}>Décision modificative</Bouton> : null}
            </div>
            <SyntheseBudget budget={b} />
            <section className={styles.bloc} aria-labelledby="titre-lignes">
              <h2 id="titre-lignes">Lignes budgétaires</h2>
              <EtatRequete chargement={lignes.isPending} erreur={lignes.error} onReessayer={() => void lignes.refetch()} lot="L4-B" fonction="Les lignes du budget">
                {(lignes.data?.elements ?? []).length === 0 ? <p className={styles.note}>Aucune ligne : saisissez-les ou importez la nomenclature (CSV).</p> : <TableauLignes lignes={lignes.data?.elements ?? []} fictif={b.fictif} />}
              </EtatRequete>
            </section>
            <DecisionsModificatives budget={b} />
            {peut(contexte, 'budget', 'exporter') ? <Exports budget={b} /> : null}
            <section className={styles.bloc} aria-labelledby="titre-journal">
              <h2 id="titre-journal">Journal des états</h2>
              <ol className={styles.journal}>
                {b.transitions.map((t, i) => <li key={i}>{dateHeure(t.le)} : {t.de ? `${libelleEtatBudget(t.de)} vers ` : ''}{libelleEtatBudget(t.vers)}{t.reference ? ` (${t.reference})` : ''}{t.motif ? `, ${t.motif}` : ''}{t.par ? `, par ${t.par}` : ''}</li>)}
              </ol>
            </section>
          </>
        )}
      </EtatRequete>
      {creation ? <FormulaireBudget exercice={exercice} onFermer={() => setCreation(false)} onCree={(id) => { setCreation(false); setChoisi(id); }} /> : null}
      {ligne && b ? <FormulaireLigne budgetId={b.id} ouverteApresAdoption={!ETATS_BUDGET_LIGNES_MODIFIABLES.includes(b.etat)} onFermer={() => setLigne(false)} /> : null}
      {dm && b ? <FormulaireDecisionModificative budgetId={b.id} lignes={lignes.data?.elements ?? []} fictif={b.fictif} onFermer={() => setDm(false)} /> : null}
      {acte && b ? <FormulaireTransition budgetId={b.id} action={acte} onFermer={() => setActe(null)} /> : null}
    </>
  );
}
